import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Text,
  useClipboard,
} from '@chakra-ui/react';
import { Form, Formik, FormikErrors } from 'formik';
import { isEmpty } from 'lodash';
import { useRouter } from 'next/router';
import React from 'react';
import { useSnackbar } from 'ui-components';
import Button from '~/components/Button';
import EmailInput from '~/components/EmailInput';
import { CrossIcon, LinkRoundedIcon } from '~/components/icons';
import { SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import { isValidEmail } from '~/utils';

import { EmailFormValue } from '../../ShareModal.models';
import { ShareModal } from '../Modal';

const validate = ({ email }: EmailFormValue) => {
  const errors: FormikErrors<EmailFormValue> = {};

  if (!isValidEmail(email)) {
    errors.email = 'Must be a valid email';
  }

  return errors;
};

interface Props {
  descriptionText: string;
  hasPassword: boolean;
  initialFocusRef: React.RefObject<HTMLInputElement>;
  modalTitle: string;
  onAddPassword: () => void;
  onCancel: () => void;
  onSubmit: (email: string) => void;
  shareableUrl: string;
}

export const EmailFormModal: React.FC<Props> = ({
  descriptionText,
  hasPassword,
  initialFocusRef,
  modalTitle,
  onAddPassword,
  onCancel,
  onSubmit,
  shareableUrl,
}) => {
  const router = useRouter();
  const { sharedToken } = router.query;
  const snackbar = useSnackbar();
  const { onCopy } = useClipboard(shareableUrl);

  const handleCopyLink = () => {
    onCopy();
    snackbar.success({
      description:
        'Link Copied: The link to this page has been copied to the clipboard',

      duration: 9000,
      isCloseable: true,
    });
  };

  const handleFinalSubmit = async ({ email }: EmailFormValue) => {
    try {
      await onSubmit(email);

      snackbar.success({
        description: `Email sent: We've sent the email to ${email}`,
        duration: 9000,
        isCloseable: true,
      });
    } catch (error) {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
    }
  };

  return (
    <Formik<EmailFormValue>
      initialValues={{ email: '' }}
      onSubmit={handleFinalSubmit}
      validate={validate}
      validateOnBlur={false}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        isSubmitting,
      }) => {
        const hasErrors = Object.values(errors?.email ?? {}).length > 0;
        const isDisabled = isEmpty(values.email) || hasErrors;

        return (
          <Form>
            <Flex justifyContent="space-between" alignItems="center">
              <ShareModal.Header flex="unset" padding={0}>
                {modalTitle}
              </ShareModal.Header>
              <ShareModal.CloseButton
                position="relative"
                top={0}
                right={0}
                width={6}
                height={6}
                fontSize={0}
              >
                <CrossIcon width="10px" height="10px" color="primary.500" />
              </ShareModal.CloseButton>
            </Flex>
            <ShareModal.Body padding={0} maxWidth="400px">
              <Divider marginTop={4} />
              <FormControl isInvalid={Boolean(errors.email) && touched.email}>
                <Text
                  marginTop={6}
                  fontWeight="normal"
                  fontSize="14px"
                  lineHeight="17px"
                >
                  {descriptionText}
                </Text>
                <Flex direction={{ base: 'column', md: 'row' }} marginTop={4}>
                  <Box minWidth={72}>
                    <EmailInput
                      id="email"
                      placeholder="Email"
                      ref={initialFocusRef}
                      value={values.email}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                    <FormErrorMessage color="error.500">
                      {errors.email}
                    </FormErrorMessage>
                  </Box>
                  <Button
                    type="submit"
                    colorScheme="primary"
                    isLoading={isSubmitting}
                    loadingText="Sending"
                    marginLeft={{ base: 0, md: 2 }}
                    minWidth={28}
                    marginTop={{ base: 6, md: 0 }}
                    isDisabled={isDisabled}
                  >
                    Send
                  </Button>
                </Flex>
              </FormControl>
            </ShareModal.Body>
            <ShareModal.Footer
              spaceBetween
              padding={0}
              dividerProps={{ marginTop: 6 }}
            >
              {sharedToken ? (
                <Button onClick={onCancel} variant="link">
                  Cancel
                </Button>
              ) : (
                <Button
                  colorScheme="primary"
                  variant="ghost"
                  size="xs"
                  onClick={onAddPassword}
                  marginLeft={-2}
                >
                  {hasPassword ? 'Edit password' : 'Add password'}
                </Button>
              )}

              {shareableUrl && (
                <Button
                  colorScheme="secondary"
                  variant="ghost"
                  size="xs"
                  onClick={handleCopyLink}
                  marginRight={-2}
                  leftIcon={<LinkRoundedIcon width={5} height={5} />}
                >
                  Copy link
                </Button>
              )}
            </ShareModal.Footer>
          </Form>
        );
      }}
    </Formik>
  );
};
